/**
 * Schlaf im Morgenjournal (Roadmap V-08).
 *
 * Schlaf laesst sich erst nach dem Aufwachen beurteilen - deshalb stehen
 * Qualitaet und Dauer im MORGENjournal und meinen die Nacht davor. Die Dauer
 * ergibt sich aus Einschlaf- und Aufwachzeit (Vorschlag aus der Roadmap).
 */
import type { JournalEntry } from '@/domain/types';

const minuten = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return h * 60 + m;
};

/**
 * Stunden zwischen Einschlafen und Aufwachen, ueber Mitternacht hinweg
 * (23:30 -> 06:45 = 7,25 h). `undefined`, wenn eine Zeit fehlt.
 */
export function schlafDauer(eingeschlafen?: string, aufgewacht?: string): number | undefined {
  if (!eingeschlafen || !aufgewacht) return undefined;
  let diff = minuten(aufgewacht) - minuten(eingeschlafen);
  if (diff <= 0) diff += 24 * 60;
  return Math.round((diff / 60) * 100) / 100;
}

/** "7 h 15 min" */
export function stundenText(stunden: number): string {
  const min = Math.round(stunden * 60);
  const h = Math.floor(min / 60);
  const rest = min % 60;
  return rest === 0 ? `${h} h` : `${h} h ${rest} min`;
}

type Zeile = Partial<Omit<JournalEntry, 'type'>> & { id: string; date: string; type: string };

/**
 * Altdaten umziehen: Schlaf, der im Abendjournal eines Tages steht, meinte die
 * Nacht VOR diesem Tag (abends laesst sich nur die vergangene Nacht
 * beurteilen) - er gehoert also ins Morgenjournal DESSELBEN Datums. Ein
 * fehlendes Morgenjournal wird angelegt; ein bereits vorhandener Schlafwert
 * dort hat Vorrang. Bleibt der Abendeintrag danach leer, entfaellt er - leere
 * Eintraege gibt es in der App nicht.
 *
 * Rein, damit das Datenbank-Upgrade und das Einspielen alter Sicherungen
 * garantiert dasselbe tun.
 */
export function schlafInsMorgenjournal<T extends Zeile>(zeilen: T[]): T[] {
  const nachId = new Map(zeilen.map((z) => [z.id, { ...z }] as const));

  for (const abend of [...nachId.values()]) {
    if (abend.type !== 'abend' || (abend.sleepHours === undefined && abend.sleepQuality === undefined)) continue;
    const morgenId = `${abend.date}:morgen`;
    const morgen = nachId.get(morgenId) ?? ({
      id: morgenId, date: abend.date, type: 'morgen', areaId: abend.areaId ?? null,
      createdAt: abend.createdAt, updatedAt: abend.updatedAt,
    } as unknown as T);
    if (morgen.sleepHours === undefined && abend.sleepHours !== undefined) morgen.sleepHours = abend.sleepHours;
    if (morgen.sleepQuality === undefined && abend.sleepQuality !== undefined) morgen.sleepQuality = abend.sleepQuality;
    nachId.set(morgenId, morgen);

    delete abend.sleepHours;
    delete abend.sleepQuality;
    const leer = abend.mood === undefined && abend.energy === undefined && !abend.body?.trim()
      && !(abend.gratitude ?? []).some((g) => g.trim());
    if (leer) nachId.delete(abend.id);
  }

  return [...nachId.values()];
}
